import React, { useState } from 'react';
import toast from 'react-hot-toast';

const LeaveTypesSettings = ({ team, setTeam, saving, handleSubmit }) => {
  const [newLeaveType, setNewLeaveType] = useState({
    name: '',
    defaultDays: 20,
    color: '#4f46e5',
    paid: true
  });

  const handleNewLeaveTypeChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewLeaveType(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addLeaveType = (e) => {
    e.preventDefault();
    
    if (!newLeaveType.name.trim()) {
      toast.error('Leave type name is required');
      return;
    }
    
    // Check for duplicate name
    if (team.leaveTypes.some(lt => lt.name.toLowerCase() === newLeaveType.name.toLowerCase())) {
      toast.error('A leave type with this name already exists');
      return;
    }
    
    const updatedLeaveTypes = [
      ...team.leaveTypes,
      { ...newLeaveType, id: Date.now().toString() }
    ];
    
    setTeam(prev => ({ ...prev, leaveTypes: updatedLeaveTypes }));
    
    // Reset form
    setNewLeaveType({
      name: '',
      defaultDays: 20,
      color: '#4f46e5',
      paid: true
    });
    
    toast.success('Leave type added');
  };

  const removeLeaveType = (id) => {
    const updatedLeaveTypes = team.leaveTypes.filter(lt => lt.id !== id);
    setTeam(prev => ({ ...prev, leaveTypes: updatedLeaveTypes }));
    toast.success('Leave type removed');
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
      <div className="p-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Leave Types</h2>
        
        <div className="space-y-6">
          {/* Current leave types */}
          {team.leaveTypes.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Default Days
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Color
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Paid
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {team.leaveTypes.map((leaveType) => (
                    <tr key={leaveType.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        <div className="flex items-center">
                          <span className="h-4 w-4 rounded-full mr-2" style={{ backgroundColor: leaveType.color }}></span>
                          {leaveType.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {leaveType.defaultDays} days
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        <div className="flex items-center">
                          <span className="h-4 w-4 rounded-full mr-2" style={{ backgroundColor: leaveType.color }}></span>
                          {leaveType.color}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {leaveType.paid ? 'Yes' : 'No'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          type="button"
                          onClick={() => removeLeaveType(leaveType.id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400">No leave types configured yet. Add your first leave type below.</p>
          )}
          
          {/* Add new leave type form */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
            <h3 className="text-md font-medium text-gray-900 dark:text-white mb-3">Add New Leave Type</h3>
            <form onSubmit={addLeaveType} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={newLeaveType.name}
                    onChange={handleNewLeaveTypeChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white sm:text-sm"
                    placeholder="e.g., Annual Leave"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="defaultDays" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Default Days
                  </label>
                  <input
                    type="number"
                    name="defaultDays"
                    id="defaultDays"
                    min="0"
                    max="365"
                    value={newLeaveType.defaultDays}
                    onChange={handleNewLeaveTypeChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white sm:text-sm"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="color" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Color
                  </label>
                  <div className="mt-1 flex items-center">
                    <input
                      type="color"
                      name="color"
                      id="color"
                      value={newLeaveType.color}
                      onChange={handleNewLeaveTypeChange}
                      className="h-8 w-8 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">{newLeaveType.color}</span>
                  </div>
                </div>
                
                <div className="flex items-center h-full mt-6">
                  <input
                    id="paid"
                    name="paid"
                    type="checkbox"
                    checked={newLeaveType.paid}
                    onChange={handleNewLeaveTypeChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="paid" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    Paid Leave
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="submit"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Add Leave Type
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      
      <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 text-right">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving}
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        >
          {saving ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </>
          ) : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default LeaveTypesSettings;
